/**
 * @file ContractsReview.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-03
 * @modified 2026-11-03
 * @description Reports → Contracts Review (Cam, 3 Nov 2026: "Add a report to
 *              reports menu that offers a summary of what was invoiced in a
 *              given month and allows the user to review whether contracts
 *              need making or changing for anything sold").
 *
 *              One row per customer and product invoiced in the month (last
 *              month by default): how much, at what price per unit, the
 *              contract the sales took on the dates sold (Pricing & Costing's
 *              own rule) with its price in the line's unit, its period and how
 *              much of it is ordered, and what is in force today and next
 *              month. Each row is flagged for what to review — no contract,
 *              another price than the contract's, a contract ending or ended,
 *              used up, on hold or archived, its product archived, several
 *              contracts — every flag a plain sentence; the rest are
 *              "Covered" (shown with "Only rows to review" off). The flag
 *              chips across the top count each flag and filter by it.
 *
 *              Acting is through the existing windows: "Contract these
 *              prices…" opens Pricing & Costing's contract window with the
 *              row's invoice lines at the price invoiced (a new contract, or a
 *              change to one of the customer's — Keep / Hold / Archive /
 *              Replace — exactly as there; needs 'Edit contract pricing'), and
 *              "Open contract" lands on the contract's own Contracts tab with
 *              it searched for. Both are always there, greyed with the reason.
 *
 *              The search screens' layout: the body scrolls inside the table
 *              under a sticky header, the totals held at its foot; pages of 50
 *              rows with every count and total the whole result's; column
 *              filters on the headers (right-click, server-side); Export to
 *              Excel is the whole result. Filters, page and column filters are
 *              kept across navigation.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './TableStyles.css';
import './ContractsReview.css';
import Dropdown from './Dropdown';
import MultiSelectDropdown from './MultiSelectDropdown';
import LoadingSpinner from './LoadingSpinner';
import PaginationControls from './PaginationControls';
import ColumnFilterMenu from './ColumnFilterMenu';
import ColumnFilterNote from './ColumnFilterNote';
import EntityLink from './EntityLink';
import SearchHelp from './SearchHelp';
import TickCheckbox from './TickCheckbox';
import ContractPricesModal from './ContractPricesModal';
import { showAlert } from './Alert';
import { useSlideOutButton } from './SlideOutButton';
import usePersistedState from '../hooks/usePersistedState';
import useLatestRequest from '../hooks/useLatestRequest';
import useColumnFilters from '../hooks/useColumnFilters';
import { useNavStore } from '../store/navStore';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatTonnes } from '../utils/numberFormatters';
import { formatDateShort } from '../utils/dateFormatters';
import {
  clearedFilters, FILTER_LABELS, reportQuery, rowActions, contractHandoff
} from '../utils/contractsReview';

const READ_URL = '/api/reports/contracts-review/contract-prices/read';
const WRITE_URL = '/api/reports/contracts-review/contract-prices';
const PAGE_SIZE = 50;

const getJson = async (url, init) => {
  const res = await fetch(url, { credentials: 'include', ...init });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) throw new Error(data.error || `The server answered ${res.status}`);
  return data;
};

const n = (v) => Number(v || 0).toLocaleString('en-GB');
const plural = (count, word) => `${n(count)} ${word}${count === 1 ? '' : 's'}`;

/** The screen's columns, each with the key its right-click filter uses (none: no filter). */
const SCREEN_COLUMNS = [
  { id: 'customer', label: 'Customer', filter: 'customer' },
  { id: 'rep', label: 'Rep', filter: 'rep' },
  { id: 'product', label: 'Product', filter: 'product' },
  { id: 'category', label: 'Category', filter: 'category' },
  { id: 'sold', label: 'Sold in the month' },
  { id: 'value', label: 'Value (ex VAT)', num: true },
  { id: 'price', label: 'Invoiced price' },
  { id: 'contract', label: 'Contract on the dates sold', filter: 'contract_state' },
  { id: 'now', label: 'Today · next month', filter: 'today' },
  { id: 'review', label: 'To review', filter: 'status' },
  { id: 'actions', label: '' }
];

/** One toggle in the filters (the system's toggle switch). */
function Toggle({ id, label, checked, onChange, title }) {
  return (
    <div className="filter-group toggle-cell" title={title}>
      <label htmlFor={id}>{label}</label>
      <div className="checkbox-container">
        <div className="check light">
          <input id={id} type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
          <label htmlFor={id}></label>
        </div>
      </div>
    </div>
  );
}

const STATE_CLASS = { Live: 'live', 'On hold': 'on-hold', Archived: 'archived', None: 'none' };

/** The contract the sales took: ref, state, price per the line's unit, period, usage. */
function ContractCell({ row }) {
  if (!row.contract) {
    return (
      <span className="cr-muted">
        None covered these sales
        {row.earlier ? <span className="cr-sub">Had {row.earlier}</span> : null}
      </span>
    );
  }
  const others = (row.contracts_taken || []).filter(c => c.key !== row.contract_key);
  return (
    <div className="cr-contract">
      <div className="cr-contract-head">
        <span className="cr-ref">{row.contract}</span>
        <span className={`cr-state ${STATE_CLASS[row.contract_state] || ''}`}>{row.contract_state}</span>
      </div>
      <span className="cr-sub">{row.contract_price}{row.contract_period ? ` · ${row.contract_period}` : ''}{row.contract_end ? ` · to ${formatDateShort(row.contract_end)}` : ''}</span>
      {row.usage_text && <span className="cr-sub">{row.usage_text}</span>}
      {(row.contract_price_notes || []).map(t => <span key={t} className="cr-sub cr-note">{t}</span>)}
      {row.covered_lines < row.sales_lines && (
        <span className="cr-sub">Took {n(row.covered_lines)} of {plural(row.sales_lines, 'sale')}</span>
      )}
      {others.length > 0 && <span className="cr-sub">Also: {others.map(c => c.ref).join(', ')}</span>}
    </div>
  );
}

function ContractsReview({ onNavigate }) {
  const [defs, setDefs] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let alive = true;
    getJson(`${API_URL}/api/reports/contracts-review/definitions`)
      .then((d) => { if (alive) setDefs(d); })
      .catch((e) => { if (alive) setLoadError(e.message); });
    return () => { alive = false; };
  }, []);

  if (loadError) return <div className="contracts-review"><div className="no-results">Could not load the report: {loadError}</div></div>;
  if (!defs) return <div className="contracts-review"><LoadingSpinner /></div>;
  return <ContractsReviewView defs={defs} onNavigate={onNavigate} />;
}

function ContractsReviewView({ defs, onNavigate }) {
  const cleared = useMemo(() => clearedFilters(defs.defaultMonth), [defs.defaultMonth]);
  const [filters, setFilterState] = usePersistedState('contractsReview.filters', cleared, { merge: true });
  const [page, setPage] = usePersistedState('contractsReview.page', 1);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [ticked, setTicked] = useState([]);
  const [contracting, setContracting] = useState(null); // { lines, title }
  const [tick, setTick] = useState(0); // a refresh asked for
  const latest = useLatestRequest();

  const colDefs = useMemo(() => Object.fromEntries((defs.columns || [])
    .filter(c => (defs.filterColumns || []).includes(c.key)).map(c => [c.key, { label: c.label }])), [defs]);
  const cols = useColumnFilters([], colDefs, {
    facets: result?.facets || {},
    storageKey: 'contractsReview.columnFilters',
    onChange: () => setPage(1)
  });

  const setFilters = (patch) => { setFilterState(prev => ({ ...prev, ...patch })); setPage(1); };
  const headerFilters = activeHeaderFilters(filters, cleared, { labels: FILTER_LABELS });
  const applied = headerFilters.count > 0 || cols.activeCount > 0;
  const handleClearFilters = () => {
    setFilterState(cleared);
    cols.clearEverything();
    setPage(1);
  };

  const query = reportQuery(filters, { columnFilters: cols.queryValue });
  const pageQuery = `${query}&page=${page || 1}&limit=${PAGE_SIZE}`;

  const runReport = useCallback(async () => {
    const ticket = latest.begin();
    const isLatest = () => latest.isLatest(ticket);
    setLoading(true);
    setError(null);
    try {
      const data = await getJson(`${API_URL}/api/reports/contracts-review?${pageQuery}`);
      if (!isLatest()) return;
      setResult(data);
      if (data.pagination && data.pagination.page !== (page || 1)) setPage(data.pagination.page);
    } catch (e) {
      if (isLatest()) { setResult(null); setError(e.message); }
    } finally {
      if (isLatest()) setLoading(false);
    }
    // `tick` re-runs it on demand (Refresh, after a contract is written).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageQuery, page, latest, setPage, tick]);

  useEffect(() => {
    const t = setTimeout(runReport, 350);
    return () => clearTimeout(t);
  }, [runReport]);

  // Ticks belong to the rows shown; a new result lets go of any it no longer has.
  useEffect(() => {
    const ids = new Set((result?.rows || []).map(r => r.id));
    setTicked(prev => prev.filter(id => ids.has(id)));
  }, [result]);

  useSlideOutButton([loading, exporting, result?.rowCount, ticked.length]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_URL}/api/reports/contracts-review/export?${query}`, { credentials: 'include' });
      if (!res.ok) {
        let message = `the server answered ${res.status}`;
        try { message = (await res.json()).error || message; } catch (e) { /* not JSON */ }
        throw new Error(message);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers.get('Content-Disposition') || '';
      const m = /filename="([^"]+)"/.exec(cd);
      a.download = m ? m[1] : 'Contracts Review.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      showAlert(`The spreadsheet could not be made: ${e.message}`, 'error');
    } finally {
      setExporting(false);
    }
  };

  const canContract = result ? !!result.canContract : !!defs.canContract;
  const contractBlock = result ? result.contractBlock : defs.contractBlock;
  const rows = result?.rows || [];
  const counts = result?.counts || null;
  const pagination = result?.pagination;
  const totals = result?.totals;

  const toggleFlag = (key) => {
    const has = (filters.flags || []).includes(key);
    setFilters({ flags: has ? filters.flags.filter(k => k !== key) : [...(filters.flags || []), key] });
  };

  const openContract = (row) => {
    const a = rowActions(row, { canContract, contractBlock }).open;
    if (a.disabled || typeof onNavigate !== 'function') return;
    const to = contractHandoff(useNavStore.getState(), a.target, a.term);
    if (to) onNavigate(to.menu, { tab: to.tab });
  };
  const contractRows = (list) => {
    const lines = [...new Set(list.flatMap(r => r.invoice_line_ids || []))].map(id => ({ id }));
    if (!lines.length) return;
    setContracting({
      lines,
      title: list.length === 1 ? `Contract these prices — ${list[0].customer}, ${list[0].item_code || list[0].product}` : `Contract these prices — ${plural(list.length, 'row')}`
    });
  };
  const tickedRows = rows.filter(r => ticked.includes(r.id));
  const tickedUsable = tickedRows.filter(r => !rowActions(r, { canContract, contractBlock }).contract.disabled);
  const allTicked = rows.length > 0 && rows.every(r => ticked.includes(r.id));

  const flagOptions = (defs.flags || []).map(f => ({
    value: f.key,
    label: `${f.label}${counts ? ` (${n(counts[f.key] || 0)})` : ''}`,
    searchText: f.label
  }));
  const monthOptions = (defs.months || []).map(m => ({ value: m.value, label: m.label }));

  return (
    <div className="contracts-review">
      <div className="search-header">
        <div className="workflow-info">
          <span className="info">Contracts Review — {result?.monthLabel || ''}</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span className="note-text">
              What was invoiced in the month, each customer&apos;s product against the contract its sales took on the dates sold, and what is in force
              today ({formatDateShort(defs.today)}) and next month — to see where a contract needs making or changing. Prices ex VAT, per each line&apos;s own unit.
            </span>
          </span>
        </div>
        <div className="search-header-inner">
          <div className="buttons slideout">
            <div>
              <button className={clearFiltersClass(applied)} onClick={handleClearFilters}
                title={applied ? 'Back to last month, rows to review, and clear the column filters' : 'Showing last month\'s rows to review'}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">filter_alt_off</span></div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={() => contractRows(tickedUsable)}
                disabled={!tickedUsable.length}
                title={!canContract ? (contractBlock || "Needs 'Edit contract pricing'")
                  : !ticked.length ? 'Tick rows to make or change their contracts together'
                    : !tickedUsable.length ? 'None of the ticked rows can be made a contract — see each row' : `Contract the prices of ${plural(tickedUsable.length, 'row')}`}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">contract_edit</span></div>
                <span className="btn-label">{ticked.length ? `Contract these prices (${n(tickedUsable.length)})` : 'Contract these prices'}</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={handleExport}
                disabled={exporting || loading || !result || result.rowCount === 0}
                title={!result || result.rowCount === 0 ? 'Nothing to export' : `Download all ${plural(result.rowCount, 'row')} as an Excel workbook`}>
                <div className="icon-wrapper"><span className={`material-symbols-rounded ${exporting ? 'spinning' : ''}`}>table_view</span></div>
                <span className="btn-label">{exporting ? 'Exporting…' : 'Export to Excel'}</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={() => setTick(t => t + 1)} disabled={loading}>
                <div className="icon-wrapper"><span className={`material-symbols-rounded ${loading ? 'spinning' : ''}`}>sync</span></div>
                <span className="btn-label">Refresh</span>
              </button>
            </div>
          </div>
          <div className="search-stats">
            {result ? `${plural(result.rowCount, 'row')} · ${plural(result.lineCount, 'invoice line')} in the month` : ''}
          </div>
        </div>
      </div>

      <div className="contracts-review-filters">
        <div className="filter-group cr-month">
          <label>Month</label>
          <Dropdown options={monthOptions} value={filters.month || defs.defaultMonth} onChange={(v) => setFilters({ month: v })} />
        </div>
        <div className="filter-group cr-search">
          <label>
            Search
            <SearchHelp fields={['customer', 'account no.', 'rep', 'item code', 'product', 'product group', 'category', 'contract number', 'invoice number', 'what to review']} />
          </label>
          <input type="text" value={filters.search || ''} placeholder="Customer, product, contract or invoice…"
            onChange={(e) => setFilters({ search: e.target.value })} />
        </div>
        <div className="filter-group cr-flags">
          <label>Flags</label>
          <MultiSelectDropdown options={flagOptions} value={filters.flags || []} onChange={(v) => setFilters({ flags: v })}
            searchable hideTags searchPlaceholder="Search flags…"
            summarize={(value) => (!value || !value.length ? 'Any flag' : value.length === 1
              ? ((defs.flags || []).find(f => f.key === value[0]) || {}).label || value[0] : `${value.length} flags`)} />
        </div>
        <div className="cr-toggles">
          <Toggle id="cr-only-review" label="Only rows to review" checked={filters.onlyReview !== false}
            onChange={(v) => setFilters({ onlyReview: v })} title="Leave out rows with nothing to review (covered) and products no contract can hold" />
          <Toggle id="cr-till" label="Till invoices" checked={!!filters.includeTill}
            onChange={(v) => setFilters({ includeTill: v })} title="The shop's sales through the till (left out by default)" />
          <Toggle id="cr-drafts" label="Draft invoices" checked={!!filters.includeDrafts}
            onChange={(v) => setFilters({ includeDrafts: v })} title="Invoices still in draft (left out by default)" />
        </div>
      </div>

      {counts && (
        <div className="cr-flag-chips" aria-label="Flags">
          <span className="cr-chip-total">
            {plural(counts.to_review, 'row')} to review · {n(counts.covered)} covered{counts.not_for_contract ? ` · ${n(counts.not_for_contract)} not for a contract` : ''}
          </span>
          {(defs.flags || []).map(f => (
            <button key={f.key} type="button" title={f.title}
              className={`cr-chip ${(filters.flags || []).includes(f.key) ? 'on' : ''} ${counts[f.key] ? '' : 'empty'}`}
              onClick={() => toggleFlag(f.key)}>
              {f.label}<span className="cr-chip-count">{n(counts[f.key] || 0)}</span>
            </button>
          ))}
        </div>
      )}

      <div className="table-toolbar contracts-review-toolbar">
        <ColumnFilterNote cols={cols} header={headerFilters} onClear={handleClearFilters} />
        {result && result.rowCount > 0 && (
          <span className="totals-scope" title="The totals count every row on these filters, on every page">
            <span className="material-symbols-rounded">functions</span>
            Totals are for all {plural(result.rowCount, 'row')}
          </span>
        )}
        {pagination && pagination.total > 0 && <PaginationControls pagination={pagination} onPageChange={(p) => setPage(p)} />}
      </div>

      <div className="table-container">
        <table className="data-table table-min-xl contracts-review-table">
          <thead>
            <tr>
              <th className="cr-tick">
                <TickCheckbox id="cr-tick-all" checked={allTicked} ariaLabel="Tick every row on this page"
                  onChange={() => setTicked(allTicked ? [] : rows.map(r => r.id))} />
              </th>
              {SCREEN_COLUMNS.map(c => {
                const hp = c.filter ? cols.headerProps(c.filter) : {};
                return (
                  <th key={c.id} data-col={c.id}
                    className={[c.num ? 'num' : '', hp.className || ''].filter(Boolean).join(' ') || undefined}
                    onContextMenu={hp.onContextMenu}
                    title={c.filter ? 'Right-click to filter this column' : undefined}>
                    {c.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {error ? (
              <tr><td colSpan={SCREEN_COLUMNS.length + 1} className="no-results">{error}</td></tr>
            ) : loading && !result ? (
              <tr><td colSpan={SCREEN_COLUMNS.length + 1}><LoadingSpinner /></td></tr>
            ) : !result || result.rowCount === 0 ? (
              <tr><td colSpan={SCREEN_COLUMNS.length + 1} className="no-results">
                {filters.onlyReview !== false ? 'Nothing to review on these filters — every product sold was covered.' : 'Nothing invoiced on these filters.'}
              </td></tr>
            ) : rows.map(r => {
              const acts = rowActions(r, { canContract, contractBlock });
              return (
                <tr key={r.id} className={r.flags.length ? 'cr-flagged' : ''}>
                  <td className="cr-tick">
                    <TickCheckbox id={`cr-tick-${r.id}`} checked={ticked.includes(r.id)} ariaLabel={`Tick ${r.customer} ${r.item_code}`}
                      onChange={() => setTicked(prev => (prev.includes(r.id) ? prev.filter(x => x !== r.id) : [...prev, r.id]))} />
                  </td>
                  <td data-col="customer">
                    <span className="name-with-badge">
                      {r.account_no && <span className="account-badge-inline">{r.account_no}</span>}
                      <EntityLink type="contact" id={r.account_id}>{r.customer}</EntityLink>
                    </span>
                  </td>
                  <td data-col="rep">{r.rep || <span className="cr-muted">No rep</span>}</td>
                  <td data-col="product">
                    <div className="cr-product">
                      {r.item_code && <span className="product-code">{r.item_code}</span>}
                      <span>{r.product}</span>
                      {r.product_group && <span className="cr-sub">Group: {r.product_group}</span>}
                      {r.item_archived && <span className="cr-sub cr-note">Product archived</span>}
                    </div>
                  </td>
                  <td data-col="category">{r.category}</td>
                  <td data-col="sold">
                    <div className="cr-sold">
                      <span>{plural(r.invoices, 'invoice')} · {plural(r.lines, 'line')}{r.credit_lines ? ` (${n(r.credit_lines)} credit)` : ''}</span>
                      <span className="cr-sub">{r.quantity}{r.tonnes != null && !/T$/.test(r.quantity) ? ` · ${formatTonnes(r.tonnes)}` : ''}</span>
                      <span className="cr-sub">{r.first_sold === r.last_sold ? formatDateShort(r.first_sold) : `${formatDateShort(r.first_sold)} – ${formatDateShort(r.last_sold)}`}</span>
                    </div>
                  </td>
                  <td data-col="value" className="num">{formatCurrency(r.value)}</td>
                  <td data-col="price">{r.price || <span className="cr-muted">—</span>}</td>
                  <td data-col="contract"><ContractCell row={r} /></td>
                  <td data-col="now">
                    <div className="cr-now">
                      <span><span className="cr-label">Today</span> {r.today}</span>
                      <span><span className="cr-label">Next month</span> {r.next_month}</span>
                    </div>
                  </td>
                  <td data-col="review">
                    {r.flags.length ? (
                      <ul className="cr-flags">
                        {r.flags.map(f => (
                          <li key={f.key}><span className={`cr-flag-chip ${f.key}`}>{f.label}</span> {f.text}</li>
                        ))}
                      </ul>
                    ) : r.not_for_contract ? (
                      <span className="cr-muted" title={r.not_for_contract}>Not for a contract</span>
                    ) : (
                      <span className="cr-covered"><span className="material-symbols-rounded">check_circle</span>Covered</span>
                    )}
                  </td>
                  <td data-col="actions">
                    <div className="actions-cell">
                      <button type="button" className={`btn-icon ${acts.contract.disabled ? 'btn-disabled' : ''}`}
                        disabled={acts.contract.disabled} title={acts.contract.title} aria-label="Contract these prices…"
                        data-write-action="true" onClick={() => contractRows([r])}>
                        <span className="material-symbols-rounded">contract_edit</span>
                      </button>
                      <button type="button" className={`btn-icon ${acts.open.disabled ? 'btn-disabled' : ''}`}
                        disabled={acts.open.disabled} title={acts.open.title} aria-label={acts.open.label}
                        onClick={() => openContract(r)}>
                        <span className="material-symbols-rounded">open_in_new</span>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {result && result.rowCount > 0 && totals && (
            <tfoot>
              <tr className="grand-total">
                <td />
                <td colSpan={4}>Total — all {plural(totals.rows, 'row')}</td>
                <td>{plural(totals.lines, 'line')}{totals.tonnes ? ` · ${formatTonnes(totals.tonnes)}` : ''}</td>
                <td className="num">{formatCurrency(totals.value)}</td>
                <td colSpan={SCREEN_COLUMNS.length - 6} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <ColumnFilterMenu cols={cols} />

      {contracting && (
        <ContractPricesModal
          kind="invoice"
          title={contracting.title}
          lines={contracting.lines}
          readUrl={READ_URL}
          writeUrl={WRITE_URL}
          onClose={() => setContracting(null)}
          onDone={(message) => {
            setContracting(null);
            showAlert(message, 'success');
            setTick(t => t + 1);
          }} />
      )}
    </div>
  );
}

export default ContractsReview;
